import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaShoppingCart, FaPlus, FaMinus, FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyBranchRequestReply = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [selectedProductUnits, setSelectedProductUnits] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [displayedProductName, setDisplayedProductName] = useState('اختر المنتج');
    const [showImageModal, setShowImageModal] = useState(false);
    const [modalImageIndex, setModalImageIndex] = useState(0);

    useEffect(() => {
        setTitle('الرد على طلب الفرع');
        if (id) {
            fetchRequestDetails();
            fetchProducts();
        }
    }, [id, setTitle, currentPage]);

    // تحديث النص المعروض عند تغيير المنتجات أو المنتج المحدد
    useEffect(() => {
        if (selectedProductId && products.length > 0) {
            const selectedProduct = products.find(p => p.id == selectedProductId);
            setDisplayedProductName(selectedProduct?.name || 'اختر المنتج');
        } else {
            setDisplayedProductName('اختر المنتج');
        }
    }, [selectedProductId, products]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchRequestDetails = async () => {
        try {
            const token = getAuthToken();
            const userRole = getUserRole();

            if (!token || userRole !== 'pharmacy_branch') {
                toast.error('غير مصرح لك بالوصول لهذه الصفحة');
                navigate('/');
                return;
            }

            const response = await axios.get(`${BASE_URL}/pharmacy/branches/requests/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                setRequest(response.data.data);
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل الطلب');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الطلب');
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الطلب');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/pharmacy/branch/products?page=${currentPage}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                const productsData = response.data.data.data || [];
                setProducts(productsData);
                setPagination(response.data.data.pagination || null);
            }
        } catch (error) {
            toast.error('فشل في تحميل المنتجات');
        }
    };

    const fetchProductDetails = async (productId) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/pharmacy/branch/products/${productId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                const productDetails = response.data.data;
                return productDetails;
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleProductSelect = async (productId) => {
        setSelectedProductId(productId);
        setSelectedUnitId('');
        setQuantity(1);

        // تحديث النص المعروض في الـ dropdown
        if (productId) {
            const selectedProduct = products.find(p => p.id == productId);
            setDisplayedProductName(selectedProduct?.name || 'اختر المنتج');

            const productDetails = await fetchProductDetails(productId);
            if (productDetails && productDetails.units) {
                setSelectedProductUnits(productDetails.units);
            } else {
                setSelectedProductUnits([]);
            }
        } else {
            setDisplayedProductName('اختر المنتج');
            setSelectedProductUnits([]);
        }
    };

    const handleAddProduct = () => {
        if (!selectedProductId || !selectedUnitId || quantity < 1) {
            toast.error('يرجى اختيار المنتج والوحدة وتحديد الكمية');
            return;
        }

        const existingIndex = selectedProducts.findIndex(
            item => item.product_id == selectedProductId && item.unit_id == selectedUnitId
        );

        const selectedProduct = products.find(p => p.id == selectedProductId);
        const selectedUnit = selectedProductUnits.find(u => u.id == selectedUnitId);



        if (existingIndex >= 0) {
            // زيادة الكمية
            const updatedProducts = [...selectedProducts];
            updatedProducts[existingIndex].quantity += quantity;
            setSelectedProducts(updatedProducts);
        } else {
            // إضافة منتج جديد
            setSelectedProducts([...selectedProducts, {
                product_id: parseInt(selectedProductId),
                unit_id: parseInt(selectedUnitId),
                quantity: quantity,
                product_name: selectedProduct?.name || 'منتج غير محدد',
                unit_name: `${selectedUnit?.unit_name || selectedUnit?.name || 'وحدة'} (${selectedUnit?.unit_type || 'غير محدد'})`
            }]);
        }

        // إعادة تعيين النموذج
        setSelectedProductId('');
        setSelectedUnitId('');
        setQuantity(1);
        setSelectedProductUnits([]);

        toast.success('تم إضافة المنتج بنجاح');
    };

    const removeProduct = (productId, unitId) => {
        setSelectedProducts(selectedProducts.filter(
            item => !(parseInt(item.product_id) == parseInt(productId) && parseInt(item.unit_id) == parseInt(unitId))
        ));
    };

    const updateQuantity = (productId, unitId, newQuantity) => {
        if (newQuantity < 1) {
            removeProduct(productId, unitId);
            return;
        }

        const updatedProducts = selectedProducts.map(item => {
            if (parseInt(item.product_id) == parseInt(productId) && parseInt(item.unit_id) == parseInt(unitId)) {
                return { ...item, quantity: newQuantity };
            }
            return item;
        });
        setSelectedProducts(updatedProducts);
    };

    const openImageModal = (index) => {
        setModalImageIndex(index);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
    };

    const nextModalImage = () => {
        if (request && request.images) {
            setModalImageIndex((prevIndex) =>
                prevIndex === request.images.length - 1 ? 0 : prevIndex + 1
            );
        }
    };

    const prevModalImage = () => {
        if (request && request.images) {
            setModalImageIndex((prevIndex) =>
                prevIndex === 0 ? request.images.length - 1 : prevIndex - 1
            );
        }
    };

    const handleSubmitReply = async () => {
        if (selectedProducts.length === 0) {
            toast.error('يرجى اختيار منتج واحد على الأقل');
            return;
        }

        try {
            setSubmitting(true);
            const token = getAuthToken();


            // إعداد البيانات بصيغة JSON مع items wrapper
            const requestData = {
                items: selectedProducts.map(item => ({
                    branch_product_id: parseInt(item.product_id),
                    quantity: parseInt(item.quantity),
                    unit_id: parseInt(item.unit_id)
                }))
            };


            await axios.post(`${BASE_URL}/pharmacy/branches/requests/doctor/reply/${id}`, requestData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'application/json',
                },
            });

            toast.success('تم إرسال الرد بنجاح');
            navigate('/pharmacy-branch-requests-new');
        } catch (error) {

            // عرض تفاصيل الخطأ إذا كانت متوفرة
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else if (error.response?.data?.errors) {
                // عرض أخطاء التحقق
                const errorMessages = Object.values(error.response.data.errors).flat();
                toast.error(errorMessages.join(', '));
            } else {
                toast.error('فشل في إرسال الرد');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="alert alert-warning text-center">
                        <h4>خطأ</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/pharmacy-branch-requests-new')}
                        >
                            العودة لطلبات الفروع
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-branch-requests-new')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                    <h2 className="mb-0" style={{ fontSize: '20px' }}>الرد على طلب الفرع</h2>
                </div>

                {/* Request Info Card */}
                {request && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="card shadow-sm mb-4"
                        style={{ borderRadius: '12px' }}>

                        <div className="card-body p-4">
                            <h5 className="mb-3 d-flex align-items-center">
                                <FaShoppingCart className="mx-2" style={{ color: '#184194' }} />
                                معلومات الطلب
                            </h5>
                            <div className="row">
                                <div className="col-md-3 mb-3">
                                    <h6 className="text-muted">اسم العميل</h6>
                                    <p className="fw-medium">{request.client?.name || (typeof request.client === 'string' ? request.client : 'غير محدد')}</p>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <h6 className="text-muted">رقم الهاتف</h6>
                                    <p className="fw-medium">{request.client?.phone || 'غير محدد'}</p>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <h6 className="text-muted">الايميل</h6>
                                    <p className="fw-medium">{request.client?.email || 'غير محدد'}</p>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <h6 className="text-muted">تاريخ الطلب</h6>
                                    <p className="fw-medium">{new Date(request.created_at).toLocaleDateString('ar-SA', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        calendar: 'gregory'
                                    })}</p>
                                </div>
                                {request.note && (
                                    <div className="col-md-3 mb-3">
                                        <h6 className="text-muted">ملاحظة العميل</h6>
                                        <p className="fw-medium">{request.note}</p>
                                    </div>
                                )}
                            </div>

                            {/* Images Section */}
                            {request.images && request.images.length > 0 && (
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <h6 className="text-muted mb-3">صور الطلب</h6>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {request.images.map((image, index) => (
                                                <div key={index} className="position-relative">
                                                    <img
                                                        src={image}
                                                        alt={`صورة الطلب ${index + 1}`}
                                                        style={{
                                                            width: '120px',
                                                            height: '120px',
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                            border: '2px solid #e9ecef',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                        onClick={() => openImageModal(index)}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.transform = 'scale(1.05)';
                                                            e.target.style.borderColor = '#184194';
                                                            e.target.style.boxShadow = '0 4px 12px rgba(24, 65, 148, 0.3)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.transform = 'scale(1)';
                                                            e.target.style.borderColor = '#e9ecef';
                                                            e.target.style.boxShadow = 'none';
                                                        }}
                                                    />
                                                    <div className="position-absolute top-0 end-0 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            transform: 'translate(8px, -8px)'
                                                        }}>
                                                        {index + 1}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <small className="text-muted mt-2 d-block">
                                            اضغط على الصورة لعرضها بحجم أكبر
                                        </small>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* Product Selection Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="card shadow-sm mb-4"
                    style={{ borderRadius: '12px' }}>

                    <div className="card-body p-4">
                        <h5 className="mb-4">اختيار المنتجات</h5>

                        <div className="row mb-4">
                            {/* Product Selection */}
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-medium">اختيار المنتج</label>
                                <div className="dropdown">
                                    <button
                                        className="btn btn-outline-secondary form-select"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{
                                            textAlign: 'right',
                                            position: 'relative',
                                            backgroundColor: 'white',
                                            border: '1px solid #ced4da'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'white';
                                            e.target.style.borderColor = '#ced4da';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'white';
                                            e.target.style.borderColor = '#ced4da';
                                        }}
                                    >
                                        <span className='text-black' style={{ paddingRight: '20px' }}>
                                            {displayedProductName}
                                        </span>

                                    </button>
                                    <ul className="dropdown-menu w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {/* Pagination Controls */}
                                        {pagination && pagination.last_page > 1 && (
                                            <li className="dropdown-item-text p-0">
                                                <div className="d-flex justify-content-between align-items-center p-3" style={{
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    borderRadius: '8px',
                                                    margin: '4px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-white mx-2" style={{ fontSize: '14px' }}>📄</span>
                                                        <small className="text-white fw-medium">
                                                            صفحة {pagination.current_page} من {pagination.last_page}
                                                        </small>
                                                    </div>
                                                    <div className="btn-group btn-group-sm shadow-sm" style={{ borderRadius: '6px', overflow: 'hidden' }}>
                                                        <button
                                                            className="btn btn-light btn-sm"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setCurrentPage(pagination.current_page - 1);
                                                            }}
                                                            disabled={pagination.current_page === 1}
                                                            style={{
                                                                padding: '6px 10px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                border: 'none',
                                                                opacity: pagination.current_page === 1 ? '0.5' : '1',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (pagination.current_page !== 1) {
                                                                    e.target.style.transform = 'scale(1.05)';
                                                                    e.target.style.backgroundColor = '#e3f2fd';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (pagination.current_page !== 1) {
                                                                    e.target.style.transform = 'scale(1)';
                                                                    e.target.style.backgroundColor = '';
                                                                }
                                                            }}
                                                        >
                                                            ▶
                                                        </button>
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            disabled
                                                            style={{
                                                                padding: '6px 12px',
                                                                fontSize: '11px',
                                                                fontWeight: '700',
                                                                backgroundColor: '#184194',
                                                                border: 'none',
                                                                borderRadius: '0'
                                                            }}
                                                        >
                                                            {pagination.current_page}
                                                        </button>
                                                        <button
                                                            className="btn btn-light btn-sm"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setCurrentPage(pagination.last_page);
                                                            }}
                                                            disabled={pagination.current_page === pagination.last_page}
                                                            style={{
                                                                padding: '6px 10px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                border: 'none',
                                                                opacity: pagination.current_page === pagination.last_page ? '0.5' : '1',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (pagination.current_page !== pagination.last_page) {
                                                                    e.target.style.transform = 'scale(1.05)';
                                                                    e.target.style.backgroundColor = '#e3f2fd';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (pagination.current_page !== pagination.last_page) {
                                                                    e.target.style.transform = 'scale(1)';
                                                                    e.target.style.backgroundColor = '';
                                                                }
                                                            }}
                                                        >
                                                            ◀
                                                        </button>
                                                    </div>
                                                </div>
                                            </li>
                                        )}

                                        {/* Products List */}
                                        {products.filter(product => product.id && product.name).map((product) => (
                                            <li key={product.id}>
                                                <button
                                                    className="dropdown-item d-flex justify-content-between align-items-center"
                                                    onClick={() => handleProductSelect(product.id)}
                                                    style={{
                                                        borderLeft: selectedProductId == product.id ? '4px solid #28a745' : '4px solid transparent',
                                                        backgroundColor: selectedProductId == product.id ? '#f8fff9' : 'transparent',
                                                        borderRadius: '6px',
                                                        margin: '2px 4px',
                                                        padding: '10px 12px',
                                                        transition: 'all 0.2s ease',
                                                        fontWeight: selectedProductId == product.id ? '600' : '400'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (selectedProductId != product.id) {
                                                            e.target.style.backgroundColor = '#f8f9fa';
                                                            e.target.style.transform = 'translateX(2px)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (selectedProductId != product.id) {
                                                            e.target.style.backgroundColor = 'transparent';
                                                            e.target.style.transform = 'translateX(0)';
                                                        }
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <span className="mx-2" style={{ fontSize: '12px' }}>🏷️</span>
                                                        <span style={{
                                                            color: selectedProductId == product.id ? '#28a745' : '#333',
                                                            fontSize: '14px'
                                                        }}>
                                                            {product.name}
                                                        </span>
                                                    </div>
                                                    <small className="text-muted" style={{
                                                        backgroundColor: '#e9ecef',
                                                        padding: '2px 6px',
                                                        borderRadius: '12px',
                                                        fontSize: '10px',
                                                        fontWeight: '500'
                                                    }}>
                                                        ID: {product.id}
                                                    </small>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Unit Selection */}
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-medium">اختيار الوحدة</label>
                                <select
                                    className="form-select"
                                    value={selectedUnitId}
                                    onChange={(e) => setSelectedUnitId(e.target.value)}
                                    disabled={!selectedProductId}
                                >
                                    <option value="">اختر الوحدة</option>
                                    {selectedProductUnits.filter(unit => unit.id).map((unit) => (
                                        <option key={unit.id} value={unit.id}>
                                            {unit.unit_name || unit.name} ({unit.unit_type || 'غير محدد'}) - {unit.price} ج.م (ID: {unit.id})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Quantity Input */}
                            <div className="col-md-2 mb-3">
                                <label className="form-label fw-medium">الكمية</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={quantity}
                                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                    min="1"
                                />
                            </div>

                            {/* Add Button */}
                            <div className="col-md-2 mb-3 d-flex align-items-end">
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={handleAddProduct}
                                    disabled={!selectedProductId || !selectedUnitId}
                                    style={{ backgroundColor: '#184194', border: 'none' }}
                                >
                                    إضافة
                                </button>
                            </div>
                        </div>

                    </div>
                </motion.div>

                {/* Selected Products Summary */}
                {selectedProducts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="card shadow-sm mb-4"
                        style={{ borderRadius: '12px' }}>

                        <div className="card-body p-4">
                            <h5 className="mb-3">المنتجات المختارة</h5>
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>اسم المنتج</th>
                                            <th>الوحدة</th>
                                            <th>الكمية</th>
                                            <th>الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProducts.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.product_name}</td>
                                                <td>{item.unit_name}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => updateQuantity(item.product_id, item.unit_id, item.quantity - 1)}
                                                            style={{ padding: '2px 6px' }}
                                                        >
                                                            <FaMinus style={{ fontSize: '10px' }} />
                                                        </button>
                                                        <span className="fw-medium" style={{ minWidth: '30px', textAlign: 'center' }}>
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => updateQuantity(item.product_id, item.unit_id, item.quantity + 1)}
                                                            style={{ padding: '2px 6px' }}
                                                        >
                                                            <FaPlus style={{ fontSize: '10px' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeProduct(item.product_id, item.unit_id)}
                                                    >
                                                        حذف
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Submit Button */}
                <div className="text-center">
                    <button
                        className="btn btn-success btn-lg"
                        onClick={handleSubmitReply}
                        disabled={selectedProducts.length === 0 || submitting}
                        style={{ backgroundColor: '#28a745', border: 'none', padding: '12px 30px' }}
                    >
                        {submitting ? 'جاري الإرسال...' : 'إرسال الرد'}
                    </button>
                </div>

                {/* Image Modal */}
                {showImageModal && request && request.images && (
                    <div
                        className="modal fade show d-block"
                        style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}
                        onClick={closeImageModal}
                    >
                        <div className="modal-dialog modal-xl modal-dialog-centered">
                            <div className="modal-content" style={{ backgroundColor: 'transparent', border: 'none' }}>
                                <div className="modal-header border-0 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={closeImageModal}
                                        style={{ fontSize: '1.5rem' }}
                                    ></button>
                                </div>
                                <div className="modal-body text-center">
                                    <div className="position-relative d-inline-block">
                                        <img
                                            src={request.images[modalImageIndex]}
                                            alt={`Request Image ${modalImageIndex + 1}`}
                                            className="img-fluid"
                                            style={{
                                                maxHeight: '80vh',
                                                maxWidth: '90vw',
                                                borderRadius: '12px',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                                            }}
                                        />

                                        {/* Navigation Buttons */}
                                        {request.images.length > 1 && (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        prevModalImage();
                                                    }}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        left: '-60px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronLeft style={{ color: '#184194', fontSize: '18px' }} />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        nextModalImage();
                                                    }}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        right: '-60px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronRight style={{ color: '#184194', fontSize: '18px' }} />
                                                </button>
                                            </>
                                        )}

                                        {/* Image Counter */}
                                        {request.images.length > 1 && (
                                            <div className="position-absolute" style={{
                                                bottom: '-50px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '8px 16px',
                                                borderRadius: '25px',
                                                fontSize: '16px',
                                                fontWeight: 'bold'
                                            }}>
                                                {modalImageIndex + 1} / {request.images.length}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PharmacyBranchRequestReply;
